/**
 * Projects Data File
 *
 * Every entry is a project. The `category` tag only groups them on the page
 * (filter tabs + badge) — it no longer changes how a card behaves.
 *
 * Project Object Structure:
 * - id: unique identifier (number)
 * - title: project title (string)
 * - category: key from `categories` below, e.g. "project", "game", "extra" (string)
 * - shortDescription: brief description for card display (string)
 * - fullDescription: detailed description for expanded view (string, optional)
 * - image: path to image in /public/images folder (string)
 * - github: link to GitHub repository (string)
 * - demo: { type: "live" | "video", url: string }
 *     "live"  → the project is hosted, the button opens it
 *     "video" → not hosted, the button opens a YouTube walkthrough
 *     empty url → button shows as "Demo soon"
 */

/**
 * Category tags. To add a new kind of entry later, add a key here and use it
 * as a project's `category`. Tabs only appear for categories that have projects.
 */
export const categories = {
  project: {
    label: 'Projects',
    name: 'Project',
    icon: 'fa-solid fa-code',
    badge: 'bg-sky-500/15 text-sky-600 dark:text-sky-300 ring-sky-500/30',
  },
  game: {
    label: 'Games',
    name: 'Game',
    icon: 'fa-solid fa-gamepad',
    badge: 'bg-fuchsia-500/15 text-fuchsia-600 dark:text-fuchsia-300 ring-fuchsia-500/30',
  },
  extra: {
    label: 'Extras',
    name: 'Extra',
    icon: 'fa-solid fa-flask',
    badge: 'bg-amber-500/15 text-amber-600 dark:text-amber-300 ring-amber-500/30',
  },
};

const projects = [
  {
    id: 9,
    title: "ABIR · عبير",
    category: "project",
    shortDescription: "A storefront for a Tunisian artisan perfume brand: the collection in French, Arabic and English, orders over WhatsApp, and an admin panel to manage products and prices.",
    fullDescription: "ABIR is a one-page site for a small Tunisian perfume house. Visitors browse the collection by category (men, women, home, car), see prices in dinars, and order any bottle in one tap through a pre-filled WhatsApp message. The site comes in French, Arabic (full right-to-left layout) and English, with a black-and-silver theme drawn from the brand's calligraphy logo and a light theme one click away. The owner gets a private admin panel to add products, upload photos, change prices, mark bottles out of stock, translate each product and read customer reviews; saved changes appear on the site within seconds. Built with Next.js, TypeScript, Tailwind CSS and Supabase on Vercel, with pages generated ahead of time for speed and every write protected by database-level security rules.",
    image: "/images/Abir.png",
    github: "",
    demo: { type: "live", url: "https://parfumeri-aboura.vercel.app/" }
  },
  {
    id: 8,
    title: "Lovenvelope",
    category: "project",
    shortDescription: "We'll handle your wedding invitation: an animated digital invite your guests open like an envelope, with online RSVP and a guest list you manage yourself. Each couple gets their own address on lovenvelope.com.",
    fullDescription: "Lovenvelope handles your wedding invitation, from the first tap to the last RSVP. Guests open an animated envelope sealed with your initials, then find your story, the schedule, the venue map, the dress code and an add-to-calendar button. Each guest can get a personal link (\"Dear John Doe…\") limited to their number of seats, and can change their answer until the deadline. The couple gets a private admin page to add guests, send personal links over WhatsApp, import or export the list as CSV and follow the answers live. Available in French, German, English, Arabic (right-to-left) and Greek, with a language switch, and designed for phones first. One site serves every couple on their own subdomain (mr-nobody-mrs-nobody.lovenvelope.com): each couple's names, texts, colors, photos and music are stored as validated settings in the database, so a new invitation needs no code, and each one can be a draft, live or switched off, with an end date after which it closes by itself. Built with Next.js, TypeScript, Tailwind CSS, Framer Motion and Supabase on Vercel, with every couple's guests and answers kept apart by database security rules.",
    image: "/images/WeddingInvite.png",
    github: "",
    demo: { type: "live", url: "https://mr-nobody-mrs-nobody.lovenvelope.com" }
  },
  {
    id: 7,
    title: "Los Pollos Hermanos",
    category: "project",
    shortDescription: "A full restaurant web app themed on the Breaking Bad chicken chain (fan project): menu with extras, live order tracking, table bookings and an admin back office.",
    fullDescription: "A full restaurant web app themed on the fictional chicken chain from Breaking Bad (fan project, not affiliated with AMC or Sony). Customers browse and filter the menu, order items with extras (sauces, sizes, spice, add-ons), check out for pickup, delivery or dine-in, track their orders live and book tables. Staff work from a live orders board and a reservations timeline; admins manage the menu, prices, photos, tables, opening hours, site content and user roles. Built with Next.js, TypeScript, Supabase (Auth, Postgres + RLS, Realtime, Storage), Tailwind and shadcn/ui on Vercel. Money is stored as integer cents and every total is computed in the database, so the browser never sends prices.",
    image: "/images/LosPollosHermanos.png",
    github: "",
    demo: { type: "live", url: "https://lospolloshermanos.achref.org" }
  },
  {
    id: 6,
    title: "Gym Progress Tracker",
    category: "project",
    shortDescription: "A full-stack fitness tracker with workout logging, nutrition, and a coach/client relationship secured by Postgres row-level security.",
    fullDescription: "A full-stack fitness application built with Next.js 15, TypeScript, Supabase (Postgres, Auth, Realtime) and Drizzle ORM. Users log workouts set by set, track calories against Mifflin-St Jeor targets using dual-source food search (USDA FoodData Central and Open Food Facts), and monitor sleep, water and body weight. Progress analytics compute estimated 1RM and weekly training volume entirely in SQL. The distinguishing feature is a coach/client relationship: coaches invite clients through an enumeration-safe lookup, view their analytics, build and assign workout programs, and message them in real time. Authorization uses a two-layer model — Server Action ownership checks on the ORM path and Postgres row-level security guarding the public API path — verified by 105 integration tests that attempt to break the permission model. Also includes an admin role with a configurable coach approval workflow, a PWA install surface, and a persistent rest timer that survives backgrounding.",
    image: "/images/GymTracker.png",
    github: "https://github.com/o-MrNobody-o/Gym-APP",
    demo: { type: "live", url: "https://gymtrack.achref.org" }
  },
  {
    id: 2,
    title: "InfraDocs Manager",
    category: "project",
    shortDescription: "A full-stack web application that centralizes IT asset management and technical documentation.",
    fullDescription: "A full-stack web application that centralizes IT asset management, software tracking, and technical documentation. It features secure LDAP authentication, role-based access control, and a modern dashboard for efficient enterprise IT operations. Built with a robust backend API and a responsive frontend interface for seamless user experience across all devices.",
    image: "/images/infraDocs.png",
    github: "https://github.com/o-MrNobody-o/Projet-Documentation-Prototype/tree/docs",
    demo: { type: "video", url: "" }
  },
  {
    id: 3,
    title: "Cupcake Mobile App",
    category: "project",
    shortDescription: "A mobile application for ordering, customizing, and reserving cupcakes with a smooth user experience.",
    fullDescription: "A mobile application designed for a fictional pastry shop called Cupcake. The app allows users to browse pastry products, customize cupcakes, place online orders, and manage reservations. It also includes client management and a structured backend following MVVM architecture with CRUD functionality. The project focuses on usability, clean design, and a seamless ordering experience tailored for mobile users.",
    image: "/images/cupcake.png",
    github: "https://github.com/o-MrNobody-o/CupcakeV1/tree/lighting",
    demo: { type: "video", url: "" }
  },
  {
    id: 5,
    title: "Memory Matching Game",
    category: "game",
    shortDescription: "A React-based Memory Matching game with multiple themes, smooth animations, and responsive gameplay.",
    fullDescription: "A front-end only Memory Matching game built with React. Players flip cards to find all matching pairs. The game features multiple selectable themes, including countries, animals, and technology. Cards have smooth flip animations, a responsive grid layout, and a move counter with a timer. The game state can be restarted at any time. The project emphasizes clean UI, modular React components, and a fun, interactive gameplay experience suitable for all ages.",
    image: "/images/MemoryGame.png",
    github: "https://github.com/o-MrNobody-o/Memory-Game",
    demo: { type: "live", url: "https://memory-game-puce-zeta.vercel.app/" }
  },
  {
    id: 4,
    title: "Hangman Game",
    category: "game",
    shortDescription: "A React-based Hangman game with bilingual support, riddles, and progress tracking.",
    fullDescription: "A front-end only Hangman game built with React. It features English and French languages, 3 levels of difficulty with progressively fewer hints, and words/phrases sourced from proverbs, riddles, or sentences. Users can track all solved riddles, which are stored in localStorage, and reset progress with a dedicated button. A small form collects the user's name and email at the start, and the game state is saved for continuity. The project emphasizes clean UI, modular components, and a fun, interactive gameplay experience.",
    image: "/images/hangman.png",
    github: "https://github.com/o-MrNobody-o/Hangman",
    demo: { type: "live", url: "https://hangman-kappa-three.vercel.app/" }
  },
  {
    id: 1,
    title: "Rock Paper Scissors",
    category: "game",
    shortDescription: "A fun and interactive Rock-Paper-Scissors game where you can play against the computer.",
    fullDescription: "A fun and interactive Rock-Paper-Scissors game where you can play against the computer. Built with HTML, CSS, and JavaScript. Features include animated hand gestures, score tracking, and a responsive design that works seamlessly on both desktop and mobile devices.",
    image: "/images/rps.png",
    github: "https://github.com/o-MrNobody-o/RockPaperScissors",
    demo: { type: "live", url: "https://o-mrnobody-o.github.io/RockPaperScissors/" }
  }
];

export default projects;
